<template>
  <v-layout
    row
    wrap
  >
    <v-flex
      md6
      xs12
    >
      <v-data-table
        :headers="tableHeaders"
        :items="financialPlan"
        hide-actions
        class="elevation-1"
      >
        <template
          slot="items"
          slot-scope="props"
        >
          <tr class="plan__field">
            <td>{{ props.item.name }}</td>
            <td class="text-right pr-0">
              <v-icon
                v-if="props.item.error"
                color="warning"
              >
                warning
              </v-icon>

              {{ props.item.error ? props.item.error : formatValue(props.item) }}

              <v-btn
                icon
                class="mr-0 edit-field"
                @click.stop="openFieldDialog(props.item)"
              >
                <v-icon>edit</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
        <template slot="footer">
          <td colspan="100%">
            <v-btn
              color="primary"
              disabled
              @click="openFieldDialog()"
            >
              Add field
            </v-btn>
            <v-btn
              color="primary"
              :loading="updatingPlan"
              @click="calculatePlan()"
            >
              <v-icon left>
                functions
              </v-icon> Calculate
            </v-btn>
          </td>
        </template>
      </v-data-table>

      <pa-dialog
        title="Field"
        negative-text
        :positive-text="editField.id ? 'Save' : 'Create'"
        :show="showFieldDialog"
        @positive="saveField"
        @cancel="closeFieldDialog"
        @negative="deleteField"
      >
        <v-flex
          xs12
          sm6
          md4
        >
          <v-text-field
            v-model="editField.name"
            label="Name"
            required
          />
        </v-flex>

        <v-flex
          xs12
          sm6
          md4
        >
          <v-text-field
            v-model="editField.variable"
            label="Variable"
            hint="To reference in formulas"
            persistent-hint
          />
        </v-flex>
        <v-flex
          xs12
          sm6
          md4
        >
          <v-select
            v-model="editField.type"
            label="Type"
            :items="fieldTypes"
          />
        </v-flex>

        <v-flex
          sm8
          xs12
        >
          <v-text-field
            v-model="editField.value"
            label="Value"
            hint="Formula starts with ="
            persistent-hint
            required
            @input="evaluateFormulaInEditor"
          />
        </v-flex>

        <v-flex
          sm4
          xs12
        >
          <v-text-field
            v-model="formulaResult"
            label="Result"
          />
        </v-flex>
      </pa-dialog>
    </v-flex>

    <v-flex
      md6
      xs12
    >
      <div
        ref="data"
        class="plan-data--container"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import axios from 'axios'
import { Parser } from 'hot-formula-parser'
import config from '@/config'
import db, { firestore } from '@/database'
import { getFilterForType } from '@/filters'
import FirebaseRebindable from '@/mixins/FirebaseRebindable'
import PaDialog from '@/components/shared/PaDialog'

export default {
  components: {
    PaDialog
  },
  mixins: [FirebaseRebindable],
  data () {
    return {
      parser: null,
      hot: null,
      genericDataRef: db.ref(),
      financialPlan: [],
      financialPlanTemplate: null,
      editField: {},
      showFieldDialog: false,
      formulaResult: null,
      updatingPlan: false,

      tableHeaders: [
        {
          text: 'Field',
          align: 'left',
          value: 'name',
          sortable: false
        },
        {
          text: 'Current',
          value: 'formattedValue',
          sortable: false
        }
      ],
      fieldTypes: [
        { text: 'None', value: null },
        { text: 'Currency', value: 'currency' },
        { text: 'Number', value: 'number' },
        { text: 'Percent', value: 'percentage' },
        { text: 'Date', value: 'date' }
      ]
    }
  },
  computed: {
    projectId () {
      return this.$route.params.name
    },
    refs () {
      return {
        financialPlan: `/financialPlan/${this.projectId}`,
        genericData: `/genericData/${this.projectId}/finance`
      }
    }
  },
  created () {
    this.parser = new Parser()
    this.parser.setVariable('__project', this.projectId)
    this.parser.on('callCellValue', this.onCallCellValue)
    this.parser.on('callRangeValue', this.onCallRangeValue)

    // =SUM_PROPS(GET_DATA('expenses/' & __project), 'estimatedCost')
    this.parser.setFunction('GET_DATA', (params) => {
      return new Promise((resolve) => {
        db.ref(params[0]).once('value', (snapshot) => {
          const array = []
          snapshot.forEach(val => { array.push(val.val()) })
          resolve(array)
        })
      })
    })

    this.parser.setFunction('SUM_PROPS', (params) => {
      return new Promise((resolve) => {
        params[0].then(result => {
          const sum = result.reduce((acc, current) => {
            return acc + current[params[1]]
          }, 0)
          resolve(sum)
        })
      })
    })
  },
  mounted () {
    this.loadFinancialPlan()
  },
  methods: {
    async loadFinancialPlan () {
      try {
        let plan = await firestore
          .doc(this.refs.financialPlan)
          .get()
        plan = plan.data()

        this.financialPlanTemplate = plan.template

        let fields = await firestore
          .doc(`/templates/financialPlan/${plan.template}/fields`)
          .get()
        fields = fields.data()
        let results = plan.fields

        this.financialPlan = []

        for (let key in fields) {
          const item = fields[key]
          let tmp = item
          let result = results[key]
          tmp.id = key
          tmp.editing = false
          tmp.result = result.result
          tmp.error = result.error
          this.financialPlan.push(tmp)

          // Init formula variables
          if (item.variable) {
            this.parser.setVariable(item.variable, result.result || item.value)
          }
        }

        let genericData = firestore
          .ref(this.refs.genericData)
          .get()

        this.hot.loadData(genericData.data())
      } catch (err) {
        this.$notifyError('Failed to load financial plan', err)
      }
    },
    evaluateFormulaInEditor () {
      let field = {
        value: this.editField.value,
        type: this.editField.type
      }

      this.evaluateField(field)
    },
    evaluateField (field) {
      // Formulas start with =
      let hasFormula = field.value && field.value[0] === '='

      if (hasFormula && field.value.length > 1) {
        let parseResult = this.parser.parse(field.value.substring(1))

        if (parseResult.error) {
          field.error = parseResult.error
          this.formulaResult = field.error
          return
        }

        Promise.resolve(parseResult.result).then(result => {
          field.result = result

          this.formulaResult = this.formatValue(field)

          if (field.variable) {
            this.parser.setVariable(field.variable, result)
          }
        })
      } else {
        field.result = field.value || 0
        this.formulaResult = this.formatValue(field)
      }
    },
    formatValue (field) {
      let filter = getFilterForType(field.type)
      let filterOpts = {}
      let formattedValue = null

      filterOpts.fractionDigits = field.decimalDigits || 0
      if (filter) {
        formattedValue = filter(field.result, filterOpts)
      } else {
        formattedValue = field.result
      }

      return formattedValue
    },
    onCallCellValue (cell, done) {
      const cellValue = this.hot.getDataAtCell(
        cell.row.index,
        cell.column.index
      )

      done(cellValue)
    },
    onCallRangeValue (fromCell, toCell, done) {
      const cellRange = this.hot.getData(
        fromCell.row.index,
        fromCell.column.index,
        toCell.row.index,
        toCell.column.index
      )

      done(cellRange)
    },
    calculatePlan () {
      this.updatingPlan = true
      axios.get(config.cloudFunctions + '/updateFinancialPlan',
        { params: { project: this.projectId } })
        .then(result => {
          this.updatingPlan = false
          this.loadFinancialPlan()
        })
        .catch(err => {
          this.updatingPlan = false
          throw err
        })
    },
    openFieldDialog (field = {}) {
      this.editField = Object.assign({}, field)
      this.editFieldOriginal = field
      this.formulaResult = null
      this.showFieldDialog = true
      this.evaluateFormulaInEditor()
    },
    closeFieldDialog () {
      this.editField = {}
      this.editFieldOriginal = {}
      this.showFieldDialog = false
    },
    saveField () {
      const toSave = {
        name: this.editField.name,
        type: this.editField.type || null,
        value: this.editField.value || 0,
        variable: this.editField.variable || null
      }

      if (this.editField.id) {
        Object.assign(this.editFieldOriginal, this.editField)
        delete this.editField.id
        delete this.editField.formattedValue
        db.ref(`/templates/financialPlan/${this.financialPlanTemplate}/fields`)
          .child(this.editFieldOriginal.id)
          .set(toSave)
          .then(() => {
            this.evaluateField(this.editFieldOriginal)
            this.closeFieldDialog()
          })
      } else {
        Object.assign(this.editFieldOriginal, this.editField)
        db.ref(`/templates/financialPlan/${this.financialPlanTemplate}/fields`)
          .push(toSave)
          .then(() => {
            this.evaluateField(this.editFieldOriginal)
            this.financialPlan.push(this.editFieldOriginal)
            this.closeFieldDialog()
          })
      }
    },
    deleteField () {
      db.ref(`/templates/financialPlan/${this.financialPlanTemplate}/fields`)
        .child(this.editFieldOriginal.id)
        .remove()
        .then(() => {
          this.financialPlan.forEach((item, i) => {
            if (item.id === this.editFieldOriginal.id) {
              this.financialPlan.splice(i, 1)
              this.closeFieldDialog()
            }
          })
        })
    }
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.loadFinancialPlan()
  }
}
</script>

<style>
.plan-data--container {
  overflow: hidden;
}

.ht_clone_top_left_corner, .ht_clone_bottom_left_corner,
.ht_master, .ht_clone_left, .ht_clone_top, .ht_clone_bottom {
  z-index: 1
}

tr.plan__field button.edit-field {
  visibility: hidden;
}

tr.plan__field:hover button.edit-field {
  visibility: visible;
}
</style>
